import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type ButtonHTMLAttributes,
} from "react";

import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full border text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus/45 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:pointer-events-none disabled:opacity-55",
  {
    variants: {
      variant: {
        primary:
          "border-primary bg-primary px-5 text-white shadow-sm hover:border-primary-strong hover:bg-primary-strong",
        secondary:
          "border-ink/20 bg-surface px-5 text-ink hover:border-primary hover:text-primary",
        quiet:
          "border-transparent bg-transparent px-4 text-ink hover:bg-ink/5 hover:text-primary",
        accent:
          "border-accent bg-accent px-5 text-white hover:border-accent-strong hover:bg-accent-strong",
        inverted:
          "border-white bg-white px-5 text-primary hover:border-white/85 hover:bg-white/85",
        danger:
          "border-danger bg-danger px-5 text-white hover:bg-danger/90",
      },
      size: {
        default: "min-h-11",
        sm: "min-h-10 px-4 text-xs",
        icon: "size-11 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
